// Elimination-derived room-player state.

import { useEffect, useMemo, useRef, useState } from 'react';
import { useRoomPlayers } from './useRoomPlayers';
import type { RoomPlayer } from '@airhead-games/shared/types';

interface UseRoomPlayerEliminationsReturn {
  eliminatedPlayers: RoomPlayer[];
  justEliminatedPlayer: RoomPlayer | null;
}

export function useRoomPlayerEliminations(): UseRoomPlayerEliminationsReturn {
  const { players } = useRoomPlayers();

  const eliminatedPlayers = useMemo(() => {
    return players.filter((player) => player.isEliminated);
  }, [players]);

  const prevEliminatedIdsRef = useRef<Set<string>>(new Set());
  const [justEliminatedPlayer, setJustEliminatedPlayer] =
    useState<RoomPlayer | null>(null);

  useEffect(() => {
    const currentEliminatedIds = new Set(
      eliminatedPlayers.map((player) => player.playerId)
    );

    const newlyEliminatedId = Array.from(currentEliminatedIds).find(
      (id) => !prevEliminatedIdsRef.current.has(id)
    );

    if (newlyEliminatedId) {
      const player = eliminatedPlayers.find(
        (candidate) => candidate.playerId === newlyEliminatedId
      );
      setJustEliminatedPlayer(player || null);
    }

    prevEliminatedIdsRef.current = currentEliminatedIds;
  }, [eliminatedPlayers]);

  return {
    eliminatedPlayers,
    justEliminatedPlayer,
  };
}
